import React, { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import Seo from "../components/Seo";
import seal from "../irofullpng.png";

const NAVY = "#004d99";
const INK = "#1a1a2e";
const CERT_W = 1000;
const CERT_H = 707; // A4 landscape ratio (1.414)

// Rasterize the seal PNG once for embedding in the jsPDF certificate.
let _sealDataUrl = null;
function loadSealDataUrl() {
  if (_sealDataUrl) return Promise.resolve(_sealDataUrl);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        // Downscale + flatten onto the cream cert background. The source seal is
        // ~2000px; embedding it full-size made jsPDF store raw RGBA (~15MB).
        // A small flattened PNG keeps the file tiny and crisp.
        const S = 240;
        const c = document.createElement("canvas");
        c.width = S;
        c.height = S;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#fdfcf8";
        ctx.fillRect(0, 0, S, S);
        ctx.drawImage(img, 0, 0, S, S);
        _sealDataUrl = c.toDataURL("image/png");
        resolve(_sealDataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = seal;
  });
}

// The printable certificate. Rendered at a fixed pixel size so the PDF capture
// is crisp and consistent regardless of the viewport.
const Certificate = React.forwardRef(({ name, level }, ref) => (
  <div
    ref={ref}
    style={{
      width: CERT_W,
      height: CERT_H,
      backgroundColor: "#fdfcf8",
      position: "relative",
      boxSizing: "border-box",
      fontFamily: "Georgia, 'Times New Roman', serif",
      color: INK,
      overflow: "hidden",
    }}
  >
    {/* Decorative double border */}
    <div
      style={{
        position: "absolute",
        inset: 22,
        border: `2px solid ${NAVY}`,
        boxSizing: "border-box",
      }}
    />
    <div
      style={{
        position: "absolute",
        inset: 30,
        border: "1px solid rgba(0,77,153,0.35)",
        boxSizing: "border-box",
      }}
    />

    {/* Content */}
    <div
      style={{
        position: "absolute",
        inset: 30,
        padding: "44px 70px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        boxSizing: "border-box",
      }}
    >
      {/* Seal */}
      <img
        src={seal}
        alt="IRO seal"
        crossOrigin="anonymous"
        style={{ width: 104, height: 104, objectFit: "contain" }}
      />

      <div
        style={{
          marginTop: 14,
          fontSize: 15,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: NAVY,
          fontWeight: 600,
          fontFamily: "Inter, Arial, sans-serif",
        }}
      >
        International Research Olympiad
      </div>

      <h1
        style={{
          margin: "18px 0 0 0",
          fontSize: 46,
          fontWeight: 700,
          letterSpacing: "0.02em",
          color: INK,
        }}
      >
        Certificate of Achievement
      </h1>

      <div
        style={{
          width: 90,
          height: 3,
          backgroundColor: "#c9a227",
          margin: "18px 0 26px 0",
        }}
      />

      <div
        style={{
          fontSize: 17,
          color: "#555",
          fontFamily: "Inter, Arial, sans-serif",
        }}
      >
        This certificate is proudly presented to
      </div>

      <div
        style={{
          marginTop: 14,
          fontSize: 52,
          fontWeight: 700,
          color: NAVY,
          lineHeight: 1.1,
          padding: "0 20px 10px 20px",
          borderBottom: "1px solid rgba(0,0,0,0.18)",
          minWidth: 420,
          maxWidth: 820,
        }}
      >
        {name || " "}
      </div>

      <div
        style={{
          marginTop: 26,
          fontSize: 17,
          color: "#555",
          fontFamily: "Inter, Arial, sans-serif",
        }}
      >
        for attaining the distinction of
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 30,
          fontWeight: 700,
          color: INK,
        }}
      >
        {level}
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 17,
          color: "#555",
          fontFamily: "Inter, Arial, sans-serif",
        }}
      >
        in the 2026 International Research Olympiad
      </div>
    </div>
  </div>
));

export default function Certificate2026() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null); // { level } ; name handled separately
  const [name, setName] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [scale, setScale] = useState(1);

  const certRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scale the fixed-size certificate down to fit the viewport for preview.
  useEffect(() => {
    const fit = () => {
      const w = stageRef.current ? stageRef.current.clientWidth : CERT_W;
      setScale(Math.min(1, w / CERT_W));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [record]);

  const lookup = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !value.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setNotFound(false);
    setRecord(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/certificate?email=${encodeURIComponent(value)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
      } else if (data.found) {
        setRecord({ level: data.level });
        setName(data.name);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setError("Could not reach the certificate service. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      // Drawn natively as vector text with jsPDF. No html2canvas, no DOM
      // capture — so it renders identically and reliably on every browser and
      // machine. The IRO brand font (Inter) is embedded so the sans-serif lines
      // match the site; serif headings use jsPDF's built-in Times.
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

      // Register the embedded Inter font (lazy-loaded to keep it off other pages).
      let SANS = "helvetica"; // fallback if the font module fails to load
      try {
        const { interRegular, interSemiBold } = await import("../fonts/inter");
        pdf.addFileToVFS("Inter-Regular.ttf", interRegular);
        pdf.addFont("Inter-Regular.ttf", "Inter", "normal");
        pdf.addFileToVFS("Inter-SemiBold.ttf", interSemiBold);
        pdf.addFont("Inter-SemiBold.ttf", "Inter", "bold");
        SANS = "Inter";
      } catch (e) {
        /* keep Helvetica fallback */
      }
      const W = pdf.internal.pageSize.getWidth();
      const H = pdf.internal.pageSize.getHeight();
      const cx = W / 2;
      const navy = [0, 77, 153];
      const ink = [26, 26, 46];
      const gold = [201, 162, 39];
      const gray = [85, 85, 85];

      // Cream background + double border
      pdf.setFillColor(253, 252, 248);
      pdf.rect(0, 0, W, H, "F");
      pdf.setDrawColor(...navy);
      pdf.setLineWidth(1.5);
      pdf.rect(18, 18, W - 36, H - 36);
      pdf.setDrawColor(150, 175, 205);
      pdf.setLineWidth(0.5);
      pdf.rect(25, 25, W - 50, H - 50);

      // Seal
      try {
        const sealData = await loadSealDataUrl();
        if (sealData) pdf.addImage(sealData, "PNG", cx - 38, 58, 76, 76);
      } catch (e) {
        /* seal optional — skip if it can't be rasterized */
      }

      // Shrink a line's font until it fits within maxWidth.
      const fit = (text, size, maxWidth, min = 14) => {
        let s = size;
        pdf.setFontSize(s);
        while (s > min && pdf.getTextWidth(text) > maxWidth) {
          s -= 1;
          pdf.setFontSize(s);
        }
        return s;
      };

      pdf.setFont(SANS, "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...navy);
      pdf.setCharSpace(3);
      pdf.text("INTERNATIONAL RESEARCH OLYMPIAD", cx, 180, { align: "center" });
      pdf.setCharSpace(0);

      pdf.setFont("times", "bold");
      pdf.setTextColor(...ink);
      fit("Certificate of Achievement", 32, W - 120);
      pdf.text("Certificate of Achievement", cx, 230, { align: "center" });

      pdf.setDrawColor(...gold);
      pdf.setLineWidth(2.5);
      pdf.line(cx - 32, 252, cx + 32, 252);

      pdf.setFont(SANS, "normal");
      pdf.setFontSize(12);
      pdf.setTextColor(...gray);
      pdf.text("This certificate is proudly presented to", cx, 298, { align: "center" });

      pdf.setFont("times", "bold");
      pdf.setTextColor(...navy);
      fit(name, 34, W - 180);
      pdf.text(name, cx, 346, { align: "center" });

      pdf.setDrawColor(195, 195, 195);
      pdf.setLineWidth(0.5);
      pdf.line(cx - 185, 362, cx + 185, 362);

      pdf.setFont(SANS, "normal");
      pdf.setFontSize(12);
      pdf.setTextColor(...gray);
      pdf.text("for attaining the distinction of", cx, 402, { align: "center" });

      pdf.setFont("times", "bold");
      pdf.setTextColor(...ink);
      fit(record.level, 20, W - 180);
      pdf.text(record.level, cx, 434, { align: "center" });

      pdf.setFont(SANS, "normal");
      pdf.setFontSize(12);
      pdf.setTextColor(...gray);
      pdf.text("in the 2026 International Research Olympiad", cx, 464, { align: "center" });

      const safe = (name || "IRO-Finalist").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "");
      pdf.save(`IRO-2026-Certificate-${safe}.pdf`);
    } catch (err) {
      setError("Sorry, the PDF could not be generated. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <Seo
        title="Certificate Portal"
        path="/certificate"
        description="Placed in the 2026 International Research Olympiad? Enter your email to view and download your official IRO certificate of achievement."
      />
      {/* Hero */}
      <div style={{ backgroundColor: "#f8f9fa", padding: "70px 0 50px 0" }}>
        <div className="container">
          <div style={{ textAlign: "center", maxWidth: "760px", margin: "0 auto" }}>
            <span
              style={{
                display: "inline-block",
                padding: "6px 14px",
                borderRadius: "6px",
                backgroundColor: "rgba(0, 77, 153, 0.08)",
                color: NAVY,
                fontSize: "0.85rem",
                fontWeight: 600,
                marginBottom: "16px",
              }}
            >
              2026 IRO
            </span>
            <h1
              style={{
                fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
                fontWeight: 700,
                color: INK,
                marginBottom: "16px",
                letterSpacing: "-0.02em",
              }}
            >
              Certificate Portal
            </h1>
            <p style={{ fontSize: "1.1rem", color: "#555", lineHeight: 1.7 }}>
              Everyone who placed in the 2026 International Research Olympiad can
              view and download a personalized certificate. Enter the email you
              used to register to find yours.
            </p>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: "40px 0 80px 0", maxWidth: 1080 }}>
        {/* Email lookup */}
        {!record && (
          <div
            style={{
              maxWidth: 520,
              margin: "0 auto",
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e8eef3",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              padding: "32px",
            }}
          >
            <form onSubmit={lookup}>
              <label
                htmlFor="cert-email"
                style={{ display: "block", fontSize: "0.95rem", fontWeight: 600, color: INK, marginBottom: 8 }}
              >
                Your email address
              </label>
              <input
                id="cert-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "1rem",
                  border: "1px solid #cdd7e1",
                  borderRadius: 8,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {error && (
                <p style={{ color: "#c0392b", fontSize: "0.9rem", marginTop: 10 }}>{error}</p>
              )}
              {notFound && (
                <p style={{ color: "#8a6d3b", fontSize: "0.92rem", marginTop: 10, lineHeight: 1.5 }}>
                  We couldn't find a 2026 IRO placement linked to that email. Double-check
                  the address you registered with. If you believe this is an error, please
                  contact the IRO team.
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 18,
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: NAVY,
                  border: "none",
                  borderRadius: 8,
                  cursor: loading ? "default" : "pointer",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Looking up…" : "View my certificate"}
              </button>
            </form>
          </div>
        )}

        {/* Certificate result */}
        {record && (
          <div>
            <div
              style={{
                maxWidth: 640,
                margin: "0 auto 28px auto",
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e8eef3",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                padding: "24px 28px",
              }}
            >
              <p style={{ margin: "0 0 14px 0", color: "#555", fontSize: "0.98rem" }}>
                You earned the distinction of{" "}
                <strong style={{ color: NAVY }}>{record.level}</strong>. Your name is shown
                below as we have it — you can correct any spelling before downloading.
              </p>
              <label
                htmlFor="cert-name"
                style={{ display: "block", fontSize: "0.9rem", fontWeight: 600, color: INK, marginBottom: 6 }}
              >
                Name on certificate
              </label>
              <input
                id="cert-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                style={{
                  width: "100%",
                  padding: "11px 13px",
                  fontSize: "1rem",
                  border: "1px solid #cdd7e1",
                  borderRadius: 8,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
                <button
                  onClick={downloadPdf}
                  disabled={downloading || !name.trim()}
                  style={{
                    padding: "11px 22px",
                    fontSize: "0.98rem",
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: NAVY,
                    border: "none",
                    borderRadius: 8,
                    cursor: downloading || !name.trim() ? "default" : "pointer",
                    opacity: downloading || !name.trim() ? 0.7 : 1,
                  }}
                >
                  {downloading ? "Preparing PDF…" : "Download PDF"}
                </button>
              </div>
              {error && (
                <p style={{ color: "#c0392b", fontSize: "0.9rem", marginTop: 12 }}>{error}</p>
              )}
            </div>

            {/* Preview (scaled to fit) */}
            <div ref={stageRef} style={{ width: "100%" }}>
              <div
                style={{
                  height: CERT_H * scale,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: "top center",
                  }}
                >
                  <Certificate ref={certRef} name={name} level={record.level} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
